import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { Business } from '../../model/business.model';
import { Job } from '../../model/job.model';
import { BusinessService } from '../../service/business.service';
import { JobService } from '../../service/job.service';
import { User } from '../../model/user.model';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BusinessDocument } from '../../model/businessDocument.model';
import { JobDocument } from '../../model/jobDocument';
import { UserService } from '../../service/user.service';
import { Subject } from 'rxjs';

@Component({
    selector: 'lib-job-form',
    templateUrl: './job-form.component.html',
    styleUrls: ['./job-form.component.scss'],
})
export class JobFormComponent implements OnInit {
    @Input() user: User;
    @Input() showTitle: boolean = true;
    @Input() textButton: string = 'Suivant';
    @Input() showButton: boolean = true;
    @Output() emitNextStep: EventEmitter<any> = new EventEmitter();

    @Input() autoSave: boolean = false;

    job_situation_options: { value: string, label: string }[] = [];

    jobs: JobDocument[] = [];
    businesses: BusinessDocument[] = [];
    correctionJob: any = null;
    correctionBusinesses: any = null;

    // Meta renvoyées par l'API (best_score, recommended_threshold) — conservées pour usage futur
    // (filtrage, analytics) ; l'affichage de l'option « suggérer un libellé » ne s'appuie pas dessus.
    bestJobScore: number | null = null;
    bestBusinessScore: number | null = null;
    recommendedJobThreshold: number | null = null;
    recommendedBusinessThreshold: number | null = null;
    lowConfidenceThreshold = 0.5;

    hasSearchedJob: boolean = false;
    hasSearchedBusiness: boolean = false;

    searchJobControl = new FormControl(null, Validators.required);
    searchBusinessControl = new FormControl(null, Validators.required);

    selectedJob: JobDocument | null = null;
    selectedBusiness: BusinessDocument | null = null;

    autreJob: JobDocument;
    autreBusiness: BusinessDocument;

    showJobSuggestionField: boolean = false;
    showBusinessSuggestionField: boolean = false;

    suggestedJobControl = new FormControl('');
    suggestedBusinessControl = new FormControl('');


    businessForm = new FormGroup({
        job_situation: new FormControl('', Validators.required),
    });

    _unsubscribeAll: Subject<any> = new Subject<any>();

    @ViewChild('jobAutocompleteTrigger') jobAutocompleteTrigger: MatAutocompleteTrigger;
    @ViewChild('businessAutocompleteTrigger') businessAutocompleteTrigger: MatAutocompleteTrigger;

    constructor(
        private jobService: JobService,
        private businessService: BusinessService,
        private userService: UserService,
        public cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.job_situation_options = this.jobService.getJobSituations();

        // Set initial values
        if (this.user) {
            this.businessForm.controls.job_situation.setValue(this.user.job_situation);

            if (this.user.job_suggested) {
                this.suggestedJobControl.setValue(this.user.job_suggested, { emitEvent: false });
                if (this.user.job) {
                    this.searchJobControl.setValue(this.user.job.name, { emitEvent: false });
                }
                // Au chargement, on affiche "autre" sélectionné et le texte de suggestion, sans ouvrir le champ d'édition
                this.showJobSuggestionField = false;
                // Charger "autre" sans sélectionner, juste pour pouvoir l'afficher si besoin
                this.getAutreJob(false, () => {
                    if (this.autreJob) {
                        // Afficher "autre" dans l'input et marquer comme sélectionné sans effacer la suggestion
                        this.searchJobControl.setValue(this.autreJob.name, { emitEvent: false });
                        this.selectedJob = this.autreJob;
                    }
                });
            } else if (this.user.job) {
                this.selectJob(this.user.job, false);
                this.fetchJobs(this.user.job.name);
            } else {
                this.fetchJobs('');
            }

            if (this.user.business_suggested) {
                this.suggestedBusinessControl.setValue(this.user.business_suggested, { emitEvent: false });
                if (this.user.business) {
                    this.searchBusinessControl.setValue(this.user.business.name, { emitEvent: false });
                }
                // Idem côté secteur : montrer la valeur enregistrée sans ouvrir le champ
                this.showBusinessSuggestionField = false;
                this.getAutreBusiness(false, () => {
                    if (this.autreBusiness) {
                        this.searchBusinessControl.setValue(this.autreBusiness.name, { emitEvent: false });
                        this.selectedBusiness = this.autreBusiness;
                    }
                });
            } else if (this.user.business) {
                this.selectBusiness(this.user.business, false);
                this.fetchBusinesses(this.user.business.name);
            } else {
                this.fetchBusinesses('');
            }
            this.cdr.detectChanges();
        } else {
            // Sans user (ex. onboarding FlashLevel) : précharger la liste triée
            // pour que l'autocomplete ne soit pas vide avant la première saisie.
            this.fetchJobs('');
            this.fetchBusinesses('');
        }

        this.searchJobControl.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
        ).subscribe(val => {
            if (typeof val === 'string' && val.trim().length != 1) {
                this.selectedJob = null;
                this.showJobSuggestionField = false;
                this.fetchJobs(val);
            }
        });

        this.searchBusinessControl.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
        ).subscribe(val => {
            if (typeof val === 'string' && val.trim().length != 1) {
                this.selectedBusiness = null;
                this.showBusinessSuggestionField = false;
                this.fetchBusinesses(val);
            }
        });

        this.businessForm.controls.job_situation.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                distinctUntilChanged(),
            )
            .subscribe(() => {
                this.cdr.detectChanges();
                this.patchUserAutoSave();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    fetchJobs(query: string): void {
        this.hasSearchedJob = false;
        this.jobService.getElasticSearch({ query, limit: 100 }).subscribe(res => {
            this.hasSearchedJob = true;
            this.jobs = res.results;
            this.bestJobScore = typeof res.best_score === 'number' ? res.best_score : null;
            this.recommendedJobThreshold = typeof (res as any).recommended_threshold === 'number' ? (res as any).recommended_threshold : null;
            this.cdr.detectChanges();
            this._scrollAutocompletePanelToTop(this.jobAutocompleteTrigger);
            if (res.results.length === 0) {
                this.jobService.getElasticSearchSuggest({ query }).subscribe(suggested => {
                    if (suggested && suggested.suggestion) {
                        this.correctionJob = suggested.suggestion;
                        this.cdr.detectChanges();
                        this._scrollAutocompletePanelToTop(this.jobAutocompleteTrigger);
                    }
                });
            }
        });
    }

    fetchBusinesses(query: string): void {
        this.hasSearchedBusiness = false;
        this.businessService.getElasticSearch({ query, limit: 100 }).subscribe(res => {
            this.hasSearchedBusiness = true;
            this.businesses = res.results;
            this.bestBusinessScore = typeof res.best_score === 'number' ? res.best_score : null;
            this.recommendedBusinessThreshold = typeof (res as any).recommended_threshold === 'number' ? (res as any).recommended_threshold : null;
            this.cdr.detectChanges();
            this._scrollAutocompletePanelToTop(this.businessAutocompleteTrigger);
            if (res.results.length === 0) {
                this.businessService.getElasticSearchSuggest({ query }).subscribe(suggested => {
                    if (suggested && suggested.suggestion) {
                        this.correctionBusinesses = suggested.suggestion;
                        this.cdr.detectChanges();
                        this._scrollAutocompletePanelToTop(this.businessAutocompleteTrigger);
                    }
                });
            }
        });
    }

    /**
     * Remet le défilement du panneau autocomplete en haut après un nouveau jeu de résultats.
     */
    private _scrollAutocompletePanelToTop(trigger: MatAutocompleteTrigger | null | undefined): void {
        if (!trigger?.panelOpen) {
            return;
        }
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const panel = document.querySelector(
                    '.cdk-overlay-container .mat-mdc-autocomplete-panel, .cdk-overlay-container .mat-autocomplete-panel'
                ) as HTMLElement | null;
                if (panel) {
                    panel.scrollTop = 0;
                }
            });
        });
    }

    selectJob(job, event?: any, save = true): void {
        // Ignore si ce n'est pas une action utilisateur (évite l'appel au chargement)
        if (event && event.isUserInput === false) {
            return;
        }
        if (!job) {
            console.warn('[selectJob] job is undefined or null');
            return;
        }
        this.selectedJob = job;
        this.suggestedJobControl.setValue(null);
        this.showJobSuggestionField = false;
        this.searchJobControl.setValue(job && job.name ? job.name : 'autre', { emitEvent: false });
        
        // Clear les erreurs après sélection
        this.searchJobControl.setErrors(null);
        this.cdr.detectChanges();

        if (save) {
            this.patchUserAutoSave();
        }
    }

    selectBusiness(business, event?: any, save = true): void {
        if (event && event.isUserInput === false) {
            return;
        }
        this.selectedBusiness = business;
        this.suggestedBusinessControl.setValue(null);
        this.showBusinessSuggestionField = false;
        this.searchBusinessControl.setValue(business.name, { emitEvent: false });
        
        // Clear les erreurs après sélection
        this.searchBusinessControl.setErrors(null);
        this.cdr.detectChanges();

        if (save) {
            this.patchUserAutoSave();
        }
    }

    canSubmit(): boolean {
        const situation = this.businessForm.controls.job_situation.value;
        const needsFields = situation === 'employed' || situation === 'unemployed';

        const jobValid = !!this.selectedJob || (this.suggestedJobControl.value && this.suggestedJobControl.value.trim().length >= 2);
        const businessValid = !!this.selectedBusiness || (this.suggestedBusinessControl.value && this.suggestedBusinessControl.value.trim().length >= 2);

        return !needsFields || (jobValid && businessValid);
    }


    submit(): void {
        const situation = this.businessForm.controls.job_situation.value;
        const payload: any = {
            job_situation: situation,
        };


        payload.job = this.selectedJob?.id ? this.selectedJob.id : this.autreJob?.id ?? null;
        payload.job_suggested = this.suggestedJobControl.value;

        payload.business = this.selectedBusiness?.id ? this.selectedBusiness.id : this.autreBusiness?.id ?? null;
        payload.business_suggested = this.suggestedBusinessControl.value;

        if (this.user) {
            this.userService.patchObservableUser(this.user.url, payload)
                .subscribe(
                    (user: User) => {
                        this.getUser();
                    }
                )
        } else {
            this.emitNextStep.emit();
        }
    }

    getUser() {
        this.userService.getObservableUserById(this.user.id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                this.cdr.detectChanges();
                this.emitNextStep.emit();
            })
    }

    displayJob(job: Job | string): string {
        const name = typeof job === 'string' ? job : job?.name ?? '';
        return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : '';
    }

    displayBusiness(business: BusinessDocument | string): string {
        const name = typeof business === 'string' ? business : business?.name ?? '';
        return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : '';
    }

    /**
     * L'option « suggérer un libellé » est proposée après une recherche ES tant que l'utilisateur
     * n'a pas choisi une entrée liste (ni valeur issue du chargement utilisateur / sélection).
     * Le template restreint encore (texte saisi, champ suggestion non ouvert).
     */
    shouldInviteJobSuggestion(): boolean {
        return this.hasSearchedJob && !this.selectedJob;
    }

    shouldInviteBusinessSuggestion(): boolean {
        return this.hasSearchedBusiness && !this.selectedBusiness;
    }


    clearJobSuggestion(): void {
        this.suggestedJobControl.setValue('');
        this.showJobSuggestionField = false;
    }

    clearBusinessSuggestion(): void {
        this.suggestedBusinessControl.setValue('');
        this.showBusinessSuggestionField = false;
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    getAutreJob(select: boolean = false, onReady?: () => void): JobDocument {
        if (!this.autreJob) {
            console.log('[getAutreJob] Fetching autreJob from API...');
            this.jobService.getElasticSearchExactAutreJob().subscribe(res => {
                console.log('[getAutreJob] API raw response:', res);
                if (res && res.id) {
                    this.autreJob = res;
                    console.log('[getAutreJob] autreJob loaded:', this.autreJob);
                    if (select) {
                        this.selectJob(this.autreJob, false);
                        this.searchJobControl.setValue(this.autreJob && this.autreJob.name ? this.autreJob.name : 'autre', { emitEvent: false });
                    }
                    this.cdr.detectChanges();
                    // Toujours appeler le callback si défini, même si select est false
                    if (onReady) onReady();
                } else {
                    console.warn('[getAutreJob] API response missing id or invalid:', res);
                }
            });
            return null;
        } else {
            console.log('[getAutreJob] autreJob already loaded:', this.autreJob);
            if (select) {
                this.selectJob(this.autreJob, false);
                this.searchJobControl.setValue(this.autreJob && this.autreJob.name ? this.autreJob.name : 'autre', { emitEvent: false });
            }
            this.cdr.detectChanges();
            if (onReady) onReady();
            return this.autreJob;
        }
    }

    getAutreBusiness(select: boolean = false, onReady?: () => void): BusinessDocument {
        if (!this.autreBusiness) {
            this.businessService.getElasticSearchExactAutreBusiness().subscribe(res => {
                if (res) {
                    this.autreBusiness = res;
                    if (select) {
                        this.selectBusiness(this.autreBusiness, false);
                        this.searchBusinessControl.setValue(this.autreBusiness.name, { emitEvent: false });
                    }
                    this.cdr.detectChanges();
                    if (onReady) onReady();
                }
            });
            return null;
        } else {
            if (select) {
                this.selectBusiness(this.autreBusiness, false);
                this.searchBusinessControl.setValue(this.autreBusiness.name, { emitEvent: false });
            }
            this.cdr.detectChanges();
            if (onReady) onReady();
            return this.autreBusiness;
        }
    }

    selectSuggestedJob(job: JobDocument): void {
        this.selectJob(job, false);
        this.searchJobControl.setValue(job.name, { emitEvent: false });
        this.fetchJobs(job.name);
    }

    selectSuggestedBusiness(business: BusinessDocument): void {
        this.selectBusiness(business, false);
        this.searchBusinessControl.setValue(business.name, { emitEvent: false });
        this.fetchBusinesses(business.name);
    }

    // Handlers pour l'option "Suggérer" dans l'autocomplete
    onJobSuggestOptionSelection(event: any): void {
        if (event && event.isUserInput) {
            this.showJobSuggestionField = true;
            const currentQuery = (this.searchJobControl.value || null).toString().trim();
            if (currentQuery) {
                this.suggestedJobControl.setValue(currentQuery, { emitEvent: false });
            }
            this.selectedJob = null;
            // Charger et sélectionner "autre", rétablir le texte saisi, puis patcher l'utilisateur
            this.getAutreJob(true, () => {
                if (this.autreJob && !this.jobs.some(j => j.id === this.autreJob.id)) {
                    this.jobs = [this.autreJob, ...this.jobs];
                }
                // Rétablir la valeur recherchée car selectJob(autre) a vidé suggestedJobControl
                if (currentQuery) {
                    this.suggestedJobControl.setValue(currentQuery, { emitEvent: false });
                }
                // this.patchUserAfterJobSuggestion(currentQuery);
            });
        }
    }

    onBusinessSuggestOptionSelection(event: any): void {
        if (event && event.isUserInput) {
            this.showBusinessSuggestionField = true;
            const currentQuery = (this.searchBusinessControl.value || '').toString().trim();
            if (currentQuery) {
                this.suggestedBusinessControl.setValue(currentQuery, { emitEvent: false });
            }
            this.selectedBusiness = null;
            // Sélectionner "autre", rétablir le texte saisi, puis patcher l'utilisateur
            this.getAutreBusiness(true, () => {
                if (currentQuery) {
                    this.suggestedBusinessControl.setValue(currentQuery, { emitEvent: false });
                }
                // this.patchUserAfterBusinessSuggestion(currentQuery);
            });
        }
    }

    // Patch immédiat de l'utilisateur après clic sur "Suggérer" (job)
    private patchUserAfterJobSuggestion(forcedText?: string): void {
        if (!this.user) {
            console.warn('[patchUserAfterJobSuggestion] No user loaded');
            return;
        }
        if (!this.autreJob) {
            console.warn('[patchUserAfterJobSuggestion] autreJob is null, aborting patch');
            return;
        }
        const payload: any = {
            job_situation: this.businessForm.controls.job_situation.value,
            job: this.autreJob.id,
            job_suggested: (forcedText && forcedText.trim()) || this.suggestedJobControl.value || (this.searchJobControl.value || '').toString().trim() || null,
        };
        console.log('[patchUserAfterJobSuggestion] PATCH payload:', payload);
        this.userService.patchObservableUser(this.user.url, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                console.log('[patchUserAfterJobSuggestion] PATCH success', user);
                this.user = user;
                this.cdr.detectChanges();
            }, err => {
                console.error('[patchUserAfterJobSuggestion] PATCH error', err);
            });
    }

    // Patch immédiat de l'utilisateur après clic sur "Suggérer" (business)
    private patchUserAfterBusinessSuggestion(forcedText?: string): void {
        if (!this.user) return;
        const payload: any = {
            job_situation: this.businessForm.controls.job_situation.value,
            business: this.autreBusiness?.id ?? null,
            business_suggested: (forcedText && forcedText.trim()) || this.suggestedBusinessControl.value || (this.searchBusinessControl.value || '').toString().trim() || null,
        };
        this.userService.patchObservableUser(this.user.url, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                this.cdr.detectChanges();
            });
    }

    patchUserAutoSave(): void {
        if (!this.autoSave || !this.user) return;

        const payload: any = {
            job_situation: this.businessForm.controls.job_situation.value,
            job: this.selectedJob?.id ?? this.autreJob?.id ?? null,
            job_suggested: this.suggestedJobControl.value,

            business: this.selectedBusiness?.id ?? this.autreBusiness?.id ?? null,
            business_suggested: this.suggestedBusinessControl.value
        };

        this.userService.patchObservableUser(this.user.url, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                this.cdr.detectChanges();
            });
    }

    /**
     * Vérifie si l'utilisateur a quitté le champ Job sans sélectionner de valeur
     */
    onJobInputBlur(): void {
        // Si aucune sélection n'a été faite, marquer le champ comme invalide
        if (!this.selectedJob && !this.suggestedJobControl.value) {
            this.searchJobControl.setErrors({ required: true });
            this.searchJobControl.markAsTouched();
            this.cdr.detectChanges();
        }
    }

    /**
     * Vérifie si l'utilisateur a quitté le champ Business sans sélectionner de valeur
     */
    onBusinessInputBlur(): void {
        // Si aucune sélection n'a été faite, marquer le champ comme invalide
        if (!this.selectedBusiness && !this.suggestedBusinessControl.value) {
            this.searchBusinessControl.setErrors({ required: true });
            this.searchBusinessControl.markAsTouched();
            this.cdr.detectChanges();
        }
    }

    closeJobAutocomplete() {
        if (this.jobAutocompleteTrigger) {
            this.jobAutocompleteTrigger.closePanel();
        }
    }
}
